import React, { useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading1, Heading2,
  Heading3, Quote, Undo2, Redo2, Image as ImageIcon, Link2, Link2Off, Minus
} from 'lucide-react';

/**
 * Reusable Tiptap-based rich text editor for tenant policy documents
 * (Política de Privacidade, Termos de Utilização). Emits HTML via onChange.
 *
 * The editor is intentionally conservative — only formatting a lawyer would
 * actually paste from a Word document (headings, bold/italic, lists, quotes,
 * images by URL, links, horizontal rule). No colours, no fonts, no tables:
 * these render poorly in every mail client and PDF exporter.
 */
const RichTextEditor = ({ value, onChange, placeholder, testid }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Image.configure({ inline: false, allowBase64: true }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class:
          'prose prose-slate max-w-none min-h-[220px] rounded-b-md border border-slate-300 border-t-0 bg-white p-4 focus:outline-none',
        'data-testid': testid || 'rich-editor-content',
        'data-placeholder': placeholder || 'Cole ou escreva aqui…',
      },
    },
    onUpdate: ({ editor: e }) => {
      const html = e.getHTML();
      // treat empty document as empty string so backend "not set" logic works
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  // Sync external value changes (e.g. when data loads after mount).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  const addImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('URL da imagem');
    if (url) editor.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  const toggleLink = useCallback(() => {
    if (!editor) return;
    const existing = editor.getAttributes('link').href;
    if (existing) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt('URL do link');
    if (url) editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="rich-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 border border-slate-300 border-b-0 rounded-t-md bg-slate-50 px-2 py-1.5">
        <ToolbarBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Negrito" testid="rte-bold"><Bold className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Itálico" testid="rte-italic"><Italic className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Rasurado"><UnderlineIcon className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Título 1"><Heading1 className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Título 2"><Heading2 className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Título 3"><Heading3 className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Lista"><List className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Lista numerada"><ListOrdered className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Citação"><Quote className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Separador"><Minus className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn active={editor.isActive('link')} onClick={toggleLink} title="Link" testid="rte-link">
          {editor.isActive('link') ? <Link2Off className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
        </ToolbarBtn>
        <ToolbarBtn onClick={addImage} title="Imagem" testid="rte-image"><ImageIcon className="w-4 h-4" /></ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} title="Desfazer"><Undo2 className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} title="Refazer"><Redo2 className="w-4 h-4" /></ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

const ToolbarBtn = ({ children, active, onClick, title, testid }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    data-testid={testid}
    className={`p-1.5 rounded hover:bg-slate-200 transition ${active ? 'bg-slate-300 text-slate-900' : 'text-slate-600'}`}
  >
    {children}
  </button>
);

const Divider = () => <span className="w-px h-5 bg-slate-300 mx-1" />;

export default RichTextEditor;
